import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { StatusBar } from '@capacitor/status-bar';

const SAFE_AREA_KEYS = {
  top: '--safe-area-top',
  right: '--safe-area-right',
  bottom: '--safe-area-bottom',
  left: '--safe-area-left',
} as const;

function syncSafeAreaWithCss(insets: Partial<Record<keyof typeof SAFE_AREA_KEYS, number>>) {
  if (typeof document === 'undefined') {
    return;
  }

  Object.entries(insets).forEach(([position, value]) => {
    const cssVar = SAFE_AREA_KEYS[position as keyof typeof SAFE_AREA_KEYS];
    document.documentElement.style.setProperty(cssVar, `${value ?? 0}px`);
  });
}

export function useCapacitorViewport() {
  useEffect(() => {
    if (Capacitor.getPlatform() === 'web') {
      syncSafeAreaWithCss({ top: 0, right: 0, bottom: 0, left: 0 });
      return;
    }

    let keyboardShowHandle: any;
    let keyboardHideHandle: any;

    StatusBar.setOverlaysWebView({ overlay: true }).catch(() => undefined);
    StatusBar.setBackgroundColor({ color: '#111827' }).catch(() => undefined);

    const setKeyboardHeight = (height: number) => {
      if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty('--keyboard-height', `${height}px`);
      }
    };

    void Keyboard.addListener('keyboardWillShow', (event) => {
      setKeyboardHeight(event.keyboardHeight ?? 0);
    }).then((handle) => {
      keyboardShowHandle = handle;
    });

    void Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardHeight(0);
    }).then((handle) => {
      keyboardHideHandle = handle;
    });

    return () => {
      if (keyboardShowHandle) {
        void keyboardShowHandle.remove();
      }

      if (keyboardHideHandle) {
        void keyboardHideHandle.remove();
      }
    };
  }, []);
}
